import { useState, useRef, useEffect, useCallback } from 'react';
import { DialogueLine, EntryTake } from '../types';

export interface UseStudioAudioGraphProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  vocalAudioRef: React.RefObject<HTMLAudioElement | null>;
  bgmAudioRef: React.RefObject<HTMLAudioElement | null>;
  takeAudioRef: React.RefObject<HTMLAudioElement | null>;
  activeTakeSourceNodeRef: React.MutableRefObject<AudioBufferSourceNode | null>;
  takeBuffersRef: React.MutableRefObject<Map<string, AudioBuffer>>;
  activePlayingTakeIdRef: React.MutableRefObject<string | null>;
  takesRef: React.MutableRefObject<EntryTake[]>;
  takes: EntryTake[];
  dialogueLinesRef: React.MutableRefObject<DialogueLine[]>;
  dialogueLines: DialogueLine[];
  selectedLineIds: Set<string>;
  duration: number;
  playbackMode: 'mixed' | 'original';
  hasStems: boolean;
  isAuditioningOriginalGuide: boolean;
  isRecordingRef: React.MutableRefObject<boolean>;
}

export interface UseStudioAudioGraphReturn {
  audioCtxRef: React.RefObject<AudioContext | null>;
  bgmGainRef: React.RefObject<GainNode | null>;
  vocalGainRef: React.RefObject<GainNode | null>;
  takeGainRef: React.RefObject<GainNode | null>;
  liveAnalyserNode: AnalyserNode | null;
  audioInputDevices: MediaDeviceInfo[];
  selectedMicDeviceId: string;
  setSelectedMicDeviceId: (deviceId: string) => Promise<void>;
  refreshMicrophoneDevices: () => Promise<void>;
  initWebAudioPipeline: () => Promise<void>;
  setupHotMicMonitoring: (targetDeviceId?: string, forceRefresh?: boolean) => Promise<void>;
  ensureHotMic: () => Promise<MediaStream | null>;
  checkSentenceMuted: (cur: number) => boolean;
  applyVocalMuting: (curTime: number, immediate?: boolean) => boolean;
  syncPlayback: (shouldPlay: boolean) => void;
}

export function useStudioAudioGraph({
  videoRef,
  vocalAudioRef,
  bgmAudioRef,
  takeAudioRef,
  activeTakeSourceNodeRef,
  takeBuffersRef,
  activePlayingTakeIdRef,
  takesRef,
  takes,
  dialogueLinesRef,
  dialogueLines,
  selectedLineIds,
  duration,
  playbackMode,
  hasStems,
  isAuditioningOriginalGuide,
  isRecordingRef
}: UseStudioAudioGraphProps): UseStudioAudioGraphReturn {
  const audioCtxRef = useRef<AudioContext | null>(null);
  const bgmGainRef = useRef<GainNode | null>(null);
  const vocalGainRef = useRef<GainNode | null>(null);
  const takeGainRef = useRef<GainNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const silentMonitorGainRef = useRef<GainNode | null>(null);
  const micSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const [liveAnalyserNode, setLiveAnalyserNode] = useState<AnalyserNode | null>(null);
  const bgmSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const vocalSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const hotMicStreamRef = useRef<MediaStream | null>(null);

  // ──────────────────────────────────────────────
  // Web Audio Graph Initialization (on User Gesture)
  // ──────────────────────────────────────────────
  const initWebAudioPipeline = useCallback(async () => {
    try {
      if (!audioCtxRef.current) {
        const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtxClass) return;
        const ctx = new AudioCtxClass();
        audioCtxRef.current = ctx;

        const bGain = ctx.createGain();
        const vGain = ctx.createGain();
        const tGain = ctx.createGain();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.3;
        analyser.minDecibels = -85;
        analyser.maxDecibels = -15;

        // Zero gain monitor: prevents any speaker echo while pulling audio continuously through the render graph
        const sGain = ctx.createGain();
        sGain.gain.value = 0.0;
        analyser.connect(sGain);
        sGain.connect(ctx.destination);

        bGain.connect(ctx.destination);
        vGain.connect(ctx.destination);
        tGain.connect(ctx.destination);

        bgmGainRef.current = bGain;
        vocalGainRef.current = vGain;
        takeGainRef.current = tGain;
        analyserRef.current = analyser;
        silentMonitorGainRef.current = sGain;
        setLiveAnalyserNode(analyser);
      }

      const ctx = audioCtxRef.current;
      if (ctx && ctx.state === 'suspended') {
        await ctx.resume().catch(() => {});
      }

      // Connect BGM element
      if (bgmAudioRef.current && !bgmSourceRef.current && ctx && bgmGainRef.current) {
        try {
          const bSrc = ctx.createMediaElementSource(bgmAudioRef.current);
          bSrc.connect(bgmGainRef.current);
          bgmSourceRef.current = bSrc;
        } catch (_) {}
      }

      // Connect Vocal element
      if (vocalAudioRef.current && !vocalSourceRef.current && ctx && vocalGainRef.current) {
        try {
          const vSrc = ctx.createMediaElementSource(vocalAudioRef.current);
          vSrc.connect(vocalGainRef.current);
          vocalSourceRef.current = vSrc;
        } catch (_) {}
      }

      // Connect Hot Mic to Analyser if available
      if (ctx && analyserRef.current && hotMicStreamRef.current && !micSourceRef.current) {
        try {
          const micSource = ctx.createMediaStreamSource(hotMicStreamRef.current);
          micSource.connect(analyserRef.current);
          micSourceRef.current = micSource;
        } catch (_) {}
      }

      // Ensure Take element has full volume (plays natively without CORS restrictions)
      if (takeAudioRef.current) {
        takeAudioRef.current.volume = 1.0;
      }
    } catch (err) {
      console.warn('Web Audio Graph initialization notice:', err);
    }
  }, [bgmAudioRef, takeAudioRef, vocalAudioRef]);

  // ──────────────────────────────────────────────
  // Persistent Hot Microphone & Audio Input Monitor
  // ──────────────────────────────────────────────
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicDeviceId, setSelectedMicDeviceIdState] = useState<string>(() => {
    return localStorage.getItem('soulsync_mic_device_id') || 'default';
  });
  const selectedMicDeviceIdRef = useRef<string>(selectedMicDeviceId);
  selectedMicDeviceIdRef.current = selectedMicDeviceId;

  const refreshMicrophoneDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter((d) => d.kind === 'audioinput');
      setAudioInputDevices(audioInputs);
    } catch (err) {
      console.warn('Enumerate audio devices note:', err);
    }
  }, []);

  const setupHotMicMonitoring = useCallback(async (targetDeviceId?: string, forceRefresh: boolean = false) => {
    try {
      const desiredDeviceId = targetDeviceId ?? selectedMicDeviceIdRef.current;
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtxClass) return;

      if (!audioCtxRef.current) {
        const ctx = new AudioCtxClass();
        audioCtxRef.current = ctx;

        const bGain = ctx.createGain();
        const vGain = ctx.createGain();
        const tGain = ctx.createGain();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.3;
        analyser.minDecibels = -85;
        analyser.maxDecibels = -15;

        // Zero gain monitor: guarantees Chrome/Edge pull frames through MediaStreamSource continuously
        const sGain = ctx.createGain();
        sGain.gain.value = 0.0;
        analyser.connect(sGain);
        sGain.connect(ctx.destination);

        bGain.connect(ctx.destination);
        vGain.connect(ctx.destination);
        tGain.connect(ctx.destination);

        bgmGainRef.current = bGain;
        vocalGainRef.current = vGain;
        takeGainRef.current = tGain;
        analyserRef.current = analyser;
        silentMonitorGainRef.current = sGain;
        setLiveAnalyserNode(analyser);
      } else if (!analyserRef.current) {
        const ctx = audioCtxRef.current;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.3;
        analyser.minDecibels = -85;
        analyser.maxDecibels = -15;

        const sGain = ctx.createGain();
        sGain.gain.value = 0.0;
        analyser.connect(sGain);
        sGain.connect(ctx.destination);

        analyserRef.current = analyser;
        silentMonitorGainRef.current = sGain;
        setLiveAnalyserNode(analyser);
      }

      const ctx = audioCtxRef.current;
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      let stream = hotMicStreamRef.current;
      const isStreamActive = stream && stream.active && stream.getAudioTracks().some((t) => t.readyState === 'live');
      const currentTrack = stream?.getAudioTracks()[0];
      const currentDeviceId = currentTrack?.getSettings?.()?.deviceId;
      const deviceMismatch = desiredDeviceId !== 'default' && currentDeviceId && currentDeviceId !== desiredDeviceId;

      if (!isStreamActive || forceRefresh || deviceMismatch) {
        // Stop old tracks so the OS releases any lock on previous hardware device
        if (hotMicStreamRef.current) {
          hotMicStreamRef.current.getTracks().forEach((t) => {
            try { t.stop(); } catch (_) {}
          });
          hotMicStreamRef.current = null;
        }

        const audioConstraints: MediaTrackConstraints = {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        };
        if (desiredDeviceId && desiredDeviceId !== 'default') {
          audioConstraints.deviceId = { exact: desiredDeviceId };
        }

        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
        } catch (_) {
          if (desiredDeviceId && desiredDeviceId !== 'default') {
            try {
              stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: desiredDeviceId } } });
            } catch {
              stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            }
          } else {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          }
        }
        hotMicStreamRef.current = stream;

        // Re-enumerate audio devices so device labels are visible now that user granted permission
        refreshMicrophoneDevices().catch(() => {});
      }

      if (ctx && analyserRef.current && stream && stream.active) {
        if (micSourceRef.current) {
          try {
            micSourceRef.current.disconnect();
          } catch (_) {}
          micSourceRef.current = null;
        }
        try {
          const micSource = ctx.createMediaStreamSource(stream);
          micSource.connect(analyserRef.current);
          micSourceRef.current = micSource;
        } catch (srcErr) {
          console.warn('MediaStreamSource connection note:', srcErr);
        }
        setLiveAnalyserNode(analyserRef.current);
      }
    } catch (err: any) {
      console.warn('Hot mic monitoring setup note:', err.message);
    }
  }, [refreshMicrophoneDevices]);

  const setSelectedMicDeviceId = useCallback(async (deviceId: string) => {
    setSelectedMicDeviceIdState(deviceId);
    selectedMicDeviceIdRef.current = deviceId;
    localStorage.setItem('soulsync_mic_device_id', deviceId);
    await setupHotMicMonitoring(deviceId, true);
  }, [setupHotMicMonitoring]);

  const ensureHotMic = useCallback(async (): Promise<MediaStream | null> => {
    await setupHotMicMonitoring();
    return hotMicStreamRef.current;
  }, [setupHotMicMonitoring]);

  // Listen for OS audio input device changes (e.g. user changes system mic in Windows)
  useEffect(() => {
    if (!navigator.mediaDevices) return;

    const handleDeviceChange = async () => {
      console.log('[AudioGraph] OS audio input device change detected');
      await refreshMicrophoneDevices();
      // If hot mic stream exists, re-acquire so Chrome binds to the newly active default or selected device
      if (hotMicStreamRef.current) {
        await setupHotMicMonitoring(selectedMicDeviceIdRef.current, true);
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    refreshMicrophoneDevices().catch(() => {});

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [refreshMicrophoneDevices, setupHotMicMonitoring]);

  // Pre-warm hot mic when entering Sync Room, and monitor input all the time
  useEffect(() => {
    setupHotMicMonitoring().catch(() => {});

    const handleUserInteraction = () => {
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      setupHotMicMonitoring().catch(() => {});
    };

    window.addEventListener('pointerdown', handleUserInteraction, { passive: true });
    window.addEventListener('click', handleUserInteraction, { passive: true });
    window.addEventListener('keydown', handleUserInteraction, { passive: true });
    window.addEventListener('touchstart', handleUserInteraction, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', handleUserInteraction);
      window.removeEventListener('click', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('touchstart', handleUserInteraction);
      if (micSourceRef.current) {
        try {
          micSourceRef.current.disconnect();
        } catch (_) {}
        micSourceRef.current = null;
      }
      if (hotMicStreamRef.current) {
        hotMicStreamRef.current.getTracks().forEach((t) => t.stop());
        hotMicStreamRef.current = null;
      }
    };
  }, [setupHotMicMonitoring]);

  // ──────────────────────────────────────────────
  // Unified Sentence Ducking & Vocal Stem Envelope (RULE: 15ms linear ramp)
  // - In 'original' mode: ONLY actively armed blocks (isDubSelected) or explicit isMutedForDubbing mute original vocals. Takes do not mute.
  // - In 'mixed' (SoulSync) mode: Actively armed blocks OR blocks with active replacement takes mute original vocals.
  // - Non-armed ranges without active takes always keep original vocals audible.
  // ──────────────────────────────────────────────
  const checkSentenceMuted = useCallback(
    (cur: number): boolean => {
      const currentTakes = takesRef.current.length > 0 ? takesRef.current : takes;
      const currentLines = dialogueLinesRef.current.length > 0 ? dialogueLinesRef.current : dialogueLines;
      const isSoulSyncMix = playbackMode === 'mixed';

      // 1. In SoulSync Mix mode: check if playhead is inside any active take's window
      if (isSoulSyncMix) {
        const isInsideActiveTake = currentTakes.some((t) => {
          if (!t.isActive) return false;
          const tStart = t.lineStart ?? 0;
          const tEnd = t.lineEnd ?? 0;
          return cur >= tStart && cur <= tEnd;
        });
        if (isInsideActiveTake) return true;
      }

      // 2. Check each dialogue line
      for (let i = 0; i < currentLines.length; i++) {
        const line = currentLines[i];
        const lineStart = line.start ?? line.startTime ?? 0;
        const lineEnd = line.end ?? line.endTime ?? duration;

        const isDubSelected = Boolean(line.id && selectedLineIds.has(line.id));
        const isMutedForDubbing = Boolean(line.isMutedForDubbing);

        let shouldMuteLine = isDubSelected || isMutedForDubbing;

        const activeTakeForLine = currentTakes.find((t) => {
          if (!t.isActive) return false;
          if (t.lineIds && line.id && t.lineIds.includes(line.id)) {
            return true;
          }
          return (
            t.lineStart !== undefined &&
            t.lineEnd !== undefined &&
            t.lineStart < lineEnd &&
            t.lineEnd > lineStart
          );
        });

        // In SoulSync Mix mode, lines with active takes also mute original vocal so take can replace it
        if (isSoulSyncMix && !shouldMuteLine && activeTakeForLine) {
          shouldMuteLine = true;
        }

        let effectiveStart = lineStart;
        let effectiveEnd = lineEnd;
        if (activeTakeForLine) {
          effectiveStart = Math.min(lineStart, activeTakeForLine.lineStart);
          effectiveEnd = Math.max(lineEnd, activeTakeForLine.lineEnd);
        }

        if (shouldMuteLine) {
          if (cur >= effectiveStart && cur <= effectiveEnd) {
            return true;
          }
        } else {
          // Line is NOT replaced: check if user trimmed dialogue block edges
          const rawStart = line.rawStart !== undefined ? line.rawStart : lineStart;
          const rawEnd = line.rawEnd !== undefined ? line.rawEnd : lineEnd;

          if (lineStart > rawStart && cur >= rawStart && cur < lineStart) {
            return true;
          }
          if (lineEnd < rawEnd && cur > lineEnd && cur <= rawEnd) {
            return true;
          }
        }
      }

      return false;
    },
    [dialogueLines, dialogueLinesRef, duration, playbackMode, selectedLineIds, takes, takesRef]
  );

  const applyVocalMuting = useCallback(
    (curTime: number, immediate: boolean = false): boolean => {
      const isSentenceMuted = checkSentenceMuted(curTime);
      const shouldMuteVocal = isAuditioningOriginalGuide ? false : isSentenceMuted;

      const ctx = audioCtxRef.current;
      if (vocalGainRef.current && ctx) {
        const targetGain = shouldMuteVocal ? 0.0 : 1.0;
        const now = ctx.currentTime;
        vocalGainRef.current.gain.cancelScheduledValues(now);
        if (immediate) {
          vocalGainRef.current.gain.setValueAtTime(targetGain, now);
        } else {
          vocalGainRef.current.gain.linearRampToValueAtTime(targetGain, now + 0.015);
        }
      }

      // CRITICAL: When Web Audio graph is running, keep audio element unmuted so Chromium
      // does not choke or starve MediaElementAudioSourceNode. VocalGainNode handles the 15ms ramp!
      if (vocalAudioRef.current) {
        if (vocalGainRef.current && ctx) {
          if (vocalAudioRef.current.muted) {
            vocalAudioRef.current.muted = false;
          }
        } else {
          vocalAudioRef.current.muted = shouldMuteVocal;
        }
      }

      // Ensure BGM element is continuously unmuted and playing at 100% gain
      if (bgmAudioRef.current) {
        if (bgmGainRef.current && ctx) {
          if (bgmAudioRef.current.muted) {
            bgmAudioRef.current.muted = false;
          }
          bgmGainRef.current.gain.value = 1.0;
        }
      }

      if (videoRef.current) {
        if (hasStems) {
          if (!videoRef.current.muted) videoRef.current.muted = true;
        } else {
          videoRef.current.muted = shouldMuteVocal;
        }
      }

      return shouldMuteVocal;
    },
    [bgmAudioRef, checkSentenceMuted, hasStems, isAuditioningOriginalGuide, videoRef, vocalAudioRef]
  );

  // ──────────────────────────────────────────────
  // Audio Playback & Synchronization (RULE: Resync drift > 0.15s)
  // ──────────────────────────────────────────────
  const syncPlayback = useCallback(
    (shouldPlay: boolean) => {
      initWebAudioPipeline();
      if (!videoRef.current) return;
      const cur = videoRef.current.currentTime;

      if (shouldPlay) {
        applyVocalMuting(cur, true);

        if (hasStems) {
          videoRef.current.muted = true;
          if (vocalAudioRef.current) {
            try { vocalAudioRef.current.currentTime = cur; } catch (_) {}
            vocalAudioRef.current.play().catch(() => {});
          }
          if (bgmAudioRef.current) {
            try { bgmAudioRef.current.currentTime = cur; } catch (_) {}
            bgmAudioRef.current.play().catch(() => {});
          }
        } else {
          videoRef.current.muted = false;
        }

        // Check if starting playback within an active recorded take
        if (isRecordingRef.current) {
          // While recording/armed, STRICTLY mute take gain node and stop any take playback
          if (takeGainRef.current) {
            takeGainRef.current.gain.cancelScheduledValues(0);
            takeGainRef.current.gain.value = 0.0;
          }
          if (activeTakeSourceNodeRef.current) {
            try {
              activeTakeSourceNodeRef.current.stop();
            } catch (_) {}
            activeTakeSourceNodeRef.current = null;
          }
          if (takeAudioRef.current && !takeAudioRef.current.paused) {
            takeAudioRef.current.pause();
          }
          activePlayingTakeIdRef.current = null;
        } else if (playbackMode !== 'original' && takesRef.current.length > 0) {
          const activeTake = takesRef.current.find((t) => t.isActive && cur >= t.lineStart && cur <= t.lineEnd);
          if (activeTake) {
            activePlayingTakeIdRef.current = activeTake.id;
            const takeAnchor = activeTake.rawStart !== undefined ? activeTake.rawStart : activeTake.lineStart;
            const offset = Math.max(0, cur - takeAnchor);
            const cachedBuffer = takeBuffersRef.current.get(activeTake.id);
            const remainingDuration = Math.max(0, activeTake.lineEnd - cur);
            const takeVol = activeTake.volume ?? 1.0;

            if (takeGainRef.current && audioCtxRef.current) {
              const now = audioCtxRef.current.currentTime;
              takeGainRef.current.gain.cancelScheduledValues(now);
              // Dynamic 50ms ramp-up at in-point (including trimmed in-points)
              takeGainRef.current.gain.setValueAtTime(0.0, now);
              takeGainRef.current.gain.linearRampToValueAtTime(takeVol, now + 0.050);

              // Dynamic 50ms ramp-down at out-point (including trimmed out-points)
              if (remainingDuration > 0.060) {
                takeGainRef.current.gain.setValueAtTime(takeVol, now + remainingDuration - 0.050);
                takeGainRef.current.gain.linearRampToValueAtTime(0.0, now + remainingDuration);
              }
            } else if (takeGainRef.current) {
              takeGainRef.current.gain.value = takeVol;
            }

            if (cachedBuffer && audioCtxRef.current && offset < cachedBuffer.duration) {
              try {
                if (activeTakeSourceNodeRef.current) {
                  activeTakeSourceNodeRef.current.stop();
                }
              } catch (_) {}
              const srcNode = audioCtxRef.current.createBufferSource();
              srcNode.buffer = cachedBuffer;
              srcNode.connect(takeGainRef.current || audioCtxRef.current.destination);
              srcNode.start(0, offset, remainingDuration);
              activeTakeSourceNodeRef.current = srcNode;
            } else if (takeAudioRef.current) {
              takeAudioRef.current.src = activeTake.localAudioUrl || activeTake.audioUrl;
              takeAudioRef.current.currentTime = offset;
              takeAudioRef.current.volume = Math.min(1.0, takeVol);
              takeAudioRef.current.play().catch(() => {});
            }
          }
        }
      } else {
        if (vocalAudioRef.current) vocalAudioRef.current.pause();
        if (bgmAudioRef.current) bgmAudioRef.current.pause();
        if (activeTakeSourceNodeRef.current) {
          try {
            if (takeGainRef.current && audioCtxRef.current) {
              const now = audioCtxRef.current.currentTime;
              takeGainRef.current.gain.cancelScheduledValues(now);
              takeGainRef.current.gain.linearRampToValueAtTime(0.0, now + 0.015);
              activeTakeSourceNodeRef.current.stop(now + 0.015);
            } else {
              activeTakeSourceNodeRef.current.stop();
            }
          } catch (_) {}
          activeTakeSourceNodeRef.current = null;
        }
        if (takeAudioRef.current) {
          takeAudioRef.current.pause();
        }
        activePlayingTakeIdRef.current = null;
      }
    },
    [
      activePlayingTakeIdRef,
      activeTakeSourceNodeRef,
      applyVocalMuting,
      bgmAudioRef,
      hasStems,
      initWebAudioPipeline,
      isRecordingRef,
      playbackMode,
      takeAudioRef,
      takeBuffersRef,
      takeGainRef,
      takesRef,
      videoRef,
      vocalAudioRef
    ]
  );

  return {
    audioCtxRef,
    bgmGainRef,
    vocalGainRef,
    takeGainRef,
    liveAnalyserNode,
    audioInputDevices,
    selectedMicDeviceId,
    setSelectedMicDeviceId,
    refreshMicrophoneDevices,
    initWebAudioPipeline,
    setupHotMicMonitoring,
    ensureHotMic,
    checkSentenceMuted,
    applyVocalMuting,
    syncPlayback
  };
}
